import {FormControl} from './FormControl';
import {ReactiveErrors} from '../utils/reactive_errors';
import {ControlContainer} from '../interfaces/ControlContainer';
import {
    ChangeDetectorRef, ContentChildren, ElementRef,
    EventEmitter,
    Host,
    HostBinding,
    Input, OnDestroy,
    OnInit,
    Optional,
    Output, QueryList,
    SkipSelf
} from '@angular/core';
import {OnChange, untilDestroyed} from '@azora/common';
import {Schema} from 'yup';
import {Subject} from 'rxjs';
import {coerceBooleanProperty} from '@angular/cdk/coercion';

export class ControlAccessor extends ControlContainer implements OnInit, OnDestroy {
    
    @Input('control')
    protected _externalControl: FormControl;
    
    @Input() name = '';
    
    @Input() type;
    @Input() label = '';
    
    @Input() debounce = 200;
    @Input() hint = '';
    @Input('mask')
    maskExpression;
    @Input() schema: Schema<any>;
    
    @Input() clearable = false;
    
    @Input() options: ({ label: any, value: any, disabled?: boolean, color?: string, labelPosition?: string, required?: boolean })[];
    
    @Output()
    change = new EventEmitter();
    
    @Output()
    modelChange: Subject<any> = new Subject();
    
    @Output()
    blur = new EventEmitter();
    @Output()
    enter = new EventEmitter();
    @Output()
    focus = new EventEmitter();
    
    /**
     * @description
     * Tracks the `FormControl` instance bound to the directive.
     */
    
    get control(): FormControl {
        return this._control;
    }
    
    set control(value: FormControl) {
        if (value && value !== this._control) {
            this._control = value;
        }
    }
    
    private _control: FormControl;
    
    /**
     * @description
     * Returns an array that represents the path from the top-level form to this control.
     * Each index is the string name of the control on that level.
     */
    get path(): string[] {
        return this.parent ? [...this.parent.path, this.name] : [];
    }
    
    get showHint(): boolean {
        return this.hint && !this.control.errors;
    }
    
    /**
     * Value for the control-group. Should equal the value of the selected option if there is
     * a corresponding option with a matching value. If there is not such a corresponding
     * option, this value persists to be applied in case a new option is added with a
     * matching value.
     */
    @Input()
    get value(): any {
        return this._value;
    }
    
    set value(newValue: any) {
        if (this._value !== newValue) {
            /**Set this before proceeding to ensure no circular loop occurs with selection.*/
            this._value = newValue;
            
            // this._updateSelectedRadioFromValue();
            // this._checkSelectedRadioButton();
        }
    }
    
    /** Whether the control is disabled */
    @Input()
    get disabled(): boolean {
        return this._disabled;
    }
    
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
    }
    
    /** Whether the control is required */
    @Input()
    get required(): boolean {
        return this._required;
    }
    
    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
        
    }
    
    @Input()
    set model(newModel: any) {
        if (this._value !== newModel) {
            /**Set this before proceeding to ensure no circular loop occurs with selection.*/
            this._value = newModel;
        }
    }
    
    /** Selected value for the control. */
    private _value: any = null;
    
    
    /** Whether the radio group is disabled. */
    private _disabled = false;
    
    /** Whether the radio group is required. */
    private _required = false;
    
    protected _nativeElement: HTMLElement;
    
    @HostBinding('attr.invalid')
    get _isControlInvalid() {
        if (this.control.invalid && (this.control.dirty)) {
            return true;
        }
        
        return this.root ? this.root.submitted : false;
    }
    
    @HostBinding('attr.focused')
    get _isFocused() {
        return this.control.focused;
    }
    
    @HostBinding('class.should-float')
    get _shouldFloat() {
        return !!this.control.value;
    }
    
    constructor(
            _elementRef: ElementRef,
            _changeDetectorRef: ChangeDetectorRef
    ) {
        super(_changeDetectorRef);
        
        this._nativeElement = _elementRef.nativeElement;
    }
    
    
    ngOnInit(): void {
        super.ngOnInit();
        
        if (!this.root) {
            this.control = this._externalControl ? this._externalControl : new FormControl();
        } else {
            if (!this.root.get(this.path)) {
                ReactiveErrors.missingNameException(this.name);
            }
            
            this.control = <FormControl>this.root.get(this.path);
        }
        
        this.control.valueChanges.subscribe(this.modelChange);
    }
    
    ngOnDestroy(): void {
    
    }
    
}
